import i18nFactory from '@yandex-int/i18n';
import * as React from 'react';

import { GridCampaignTypeCell } from '../../components/GridCampaignTypeCell/GridCampaignTypeCell';
import { GridParentCell } from '../../components/GridParentCell/GridParentCell';
import { GridRetargetingDescriptionCell } from '../../components/GridRetargetingDescriptionCell/GridRetargetingDescriptionCell';
import { GridRetargetingNameCell } from '../../components/GridRetargetingNameCell/GridRetargetingNameCell';
import { GridRetargetingStatusCell } from '../../components/GridRetargetingStatusCell/GridRetargetingStatusCell';
import { MoneyCell } from '../../components/MoneyCell/MoneyCell';
import { TextCell } from '../../components/TextCell/TextCell';
import { GridActionsMenuContainer } from '../../containers/GridActionsMenuContainer/GridActionsMenuContainer';
import { RetargetingMoneyCellEditorContainer } from '../../containers/RetargetingMoneyCellEditorContainer/RetargetingMoneyCellEditorContainer';
import { RetargetingRulesInfoContainer } from '../../containers/RetargetingRulesInfoContainer/RetargetingRulesInfoContainer';
import {
    getDynamicStatsColumnsFields,
    getStaticStatsColumns,
    StatKey
} from '../../declarations/stats/stats';
import { mapStringToCurrencySymbol } from '../../mappers/common/common';
import {
    addSettingsToDecl,
    arrangeInOrder
} from '../../service/DeclarationHelper';
import { isStatusTextVisible } from '../../service/GridHelpers/GridHelpers';
import {
    ColumnsSettings,
    ColumnsSettingsViewDict,
    retargetings
} from '../../typings/common';
import { ConstData } from '../../typings/constants';
import { Retargeting } from '../../typings/domain';
import { CurrencyCode } from '../../typings/graphql-grid';
import {
    GridCellProps,
    GridColumnDecl,
    GridColumnsOptions,
    GridRetargetingItemActions,
    GridScreenTotalKey,
    OptionalGridColumnDecl
} from '../../typings/grid';

import * as keyset from './targeting.i18n';

const i18n = i18nFactory(keyset);

export enum RetargetingColumnKey {
    name = 'name',
    settings = 'settings',
    status = 'status',
    targeting = 'targeting',
    campaignType = 'campaign-type',
    campaignGroup = 'campaign_id',
    contextPrice = 'price_context',
    description = 'description'
}

const getFixedRetargetingsColumns = (): Array<
    OptionalGridColumnDecl<Retargeting, {}, GridColumnsOptions>
> => {
    return [
        {
            title: '',
            key: RetargetingColumnKey.settings,
            cellClass: 'settings-cell',
            isSortable: false,
            mandatory: true,
            fixed: true,
            isAlignedToLeft: true,
            minWidth: 35,
            maxWidth: 35,
            CellComponent: React.memo(
                ({
                    rowIndex,
                    actions
                }: GridCellProps<
                    Retargeting,
                    GridRetargetingItemActions
                >): JSX.Element => (
                    <GridActionsMenuContainer
                        index={rowIndex}
                        actions={actions}
                    />
                )
            )
        },
        {
            title: i18n('name'),
            key: RetargetingColumnKey.name,
            fixed: true,
            totalKey: GridScreenTotalKey.name,
            minWidth: 300,
            maxWidth: 700,
            CellComponent: React.memo(
                ({
                    rowData: { id, name }
                }: GridCellProps<Retargeting>): JSX.Element => (
                    <GridRetargetingNameCell id={id} name={name} />
                )
            )
        }
    ];
};

export const getRetargetingsColumnsBase = (
    clientCurrency: CurrencyCode,
    isCompactViewEnabled: boolean,
    columnsSettings: ColumnsSettings,
    constants?: ConstData
): Array<OptionalGridColumnDecl<Retargeting, {}, GridColumnsOptions>> => {
    const currency = mapStringToCurrencySymbol(clientCurrency);

    return [
        {
            title: i18n('status'),
            isSortable: false,
            key: RetargetingColumnKey.status,
            minWidth: 35,
            maxWidth: 300,
            CellComponent: React.memo(
                ({
                    rowData: { status }
                }: GridCellProps<Retargeting>): JSX.Element => {
                    const columnViewInfo =
                        columnsSettings.columnViewInfo[
                            RetargetingColumnKey.status
                        ];

                    return (
                        <GridRetargetingStatusCell
                            status={status}
                            isTextVisible={isStatusTextVisible(
                                isCompactViewEnabled,
                                columnViewInfo
                            )}
                        />
                    );
                }
            )
        },
        {
            title: i18n('targeting'),
            isSortable: false,
            key: RetargetingColumnKey.targeting,
            minWidth: 230,
            maxWidth: 1060,
            CellComponent: React.memo(
                ({
                    rowData: { conditionRules, id }
                }: GridCellProps<Retargeting>): JSX.Element => (
                    <RetargetingRulesInfoContainer
                        key={id}
                        conditionRules={conditionRules}
                    />
                )
            )
        },
        {
            title: i18n('campaign-type'),
            key: RetargetingColumnKey.campaignType,
            isSortable: false,
            minWidth: 35,
            maxWidth: 105,
            align: 'right',
            CellComponent: React.memo(
                ({
                    rowData: {
                        group: {
                            campaign: { type: campaignType }
                        }
                    }
                }: GridCellProps<Retargeting>): JSX.Element => {
                    return <GridCampaignTypeCell type={campaignType} />;
                }
            )
        },
        {
            title: i18n('campaign-group'),
            key: RetargetingColumnKey.campaignGroup,
            minWidth: 300,
            maxWidth: 600,
            CellComponent: React.memo(
                ({
                    rowData: { group }
                }: GridCellProps<Retargeting>): JSX.Element => (
                    <GridParentCell campaign={group.campaign} group={group} />
                )
            )
        },
        {
            title: i18n('context-price-with-currency', { currency }),
            titleSettings: i18n('context-price'),
            key: RetargetingColumnKey.contextPrice,
            align: 'left',
            minWidth: 90,
            maxWidth: 200,
            CellComponent: React.memo(
                ({
                    rowData: {
                        priceContext,
                        id,
                        isPriceContextEnabled,
                        isEditable
                    }
                }: GridCellProps<Retargeting>): JSX.Element => {
                    if (!isPriceContextEnabled) {
                        return <TextCell>{'–'}</TextCell>;
                    }

                    if (!constants || !isEditable) {
                        return <MoneyCell num={priceContext} />;
                    }

                    return (
                        <RetargetingMoneyCellEditorContainer
                            constants={constants}
                            bid={priceContext}
                            id={id}
                            key={id}
                            currency={clientCurrency}
                            cellName={RetargetingColumnKey.contextPrice}
                        />
                    );
                }
            )
        },
        {
            title: i18n('description'),
            isSortable: false,
            key: RetargetingColumnKey.description,
            minWidth: 95,
            maxWidth: 600,

            CellComponent: React.memo(
                ({
                    rowData: { description }
                }: GridCellProps<Retargeting>): JSX.Element => (
                    <GridRetargetingDescriptionCell text={description} />
                )
            )
        }
    ];
};

export const retargetingsColumnOrder = [
    'settings',
    'id',
    'status',
    'targeting',
    'campaign-type',
    'campaign_id',
    'price_context',
    'description',
    'STAT_COST',
    'STAT_GOALS',
    'STAT_REVENUE',
    'STAT_PROFITABILITY',
    'STAT_AVG_GOAL_COST',
    'STAT_AVG_CLICK_COST',
    'STAT_SHOWS',
    'STAT_CLICKS',
    'STAT_CTR',
    'STAT_CONVERSION_RATE',
    'STAT_BOUNCE_RATE'
];

export function getDefaultDynamicRetargetingColumnsFields(): ColumnsSettings {
    return {
        columnOrder: retargetingsColumnOrder,
        columnViewInfo: {
            settings: {
                width: 35,
                widthCompact: 35,
                hidden: false
            },
            name: {
                width: 450,
                widthCompact: 450,
                hidden: false
            },
            status: {
                width: 200,
                widthCompact: 200,
                hidden: false
            },
            targeting: {
                width: 400,
                widthCompact: 400,
                hidden: false
            },
            'campaign-type': {
                width: 35,
                widthCompact: 35,
                hidden: false
            },
            campaign_id: {
                width: 300,
                widthCompact: 300,
                hidden: false
            },
            price_context: {
                width: 150,
                widthCompact: 150,
                hidden: false
            },
            description: {
                width: 400,
                widthCompact: 400,
                hidden: false
            },
            ...getDynamicStatsColumnsFields()
        }
    };
}

export function getRetargetingColumns(
    options: GridColumnsOptions,
    columnsSettings: ColumnsSettingsViewDict,
    clientCurrency: CurrencyCode,
    isCompactViewEnabled: boolean,
    isGoalsStatEnabled: boolean,
    hasSelectedGoals: boolean,
    constants?: ConstData,
    clientLogin?: string,
    hasSelectedGoalsAtColumnsSettingsEditor?: boolean,
    isColumnsSettingsEditorLocation?: boolean
): Array<GridColumnDecl<Retargeting>> {
    const fixedColumns = getFixedRetargetingsColumns().map(column => {
        return addSettingsToDecl(column, columnsSettings, retargetings);
    });

    const result = getRetargetingsColumnsBase(
        clientCurrency,
        isCompactViewEnabled,
        columnsSettings[retargetings],
        constants
    )
        .concat(
            getStaticStatsColumns<Retargeting>(
                {
                    getCurrency: (item: Retargeting): CurrencyCode =>
                        item.group.campaign.currency
                },
                clientCurrency,
                isCompactViewEnabled,
                hasSelectedGoals,
                isGoalsStatEnabled,
                hasSelectedGoalsAtColumnsSettingsEditor,
                isColumnsSettingsEditorLocation
            )
        )
        .filter(column => {
            if (column.key === StatKey.STAT_CPM_PRICE) {
                return false;
            }
            if (column.key === StatKey.STAT_AVG_CLICK_COST) {
                return true;
            }
            return column.isAvailable ? column.isAvailable(options) : true;
        })
        .map(column => {
            return {
                ...column,
                ...columnsSettings[retargetings].columnViewInfo[column.key]
            };
        });
    return fixedColumns.concat(
        arrangeInOrder(result, columnsSettings[retargetings].columnOrder, true)
    );
}
